import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { UseFormReturn } from "react-hook-form";

import { FeatureType } from "src/clients/features-control";
import {
  autoAssignColumnTypes,
  ColumnType,
  getEntityColumnFromTypes,
  handleFeatureGenerationFromColumnTypes,
  MIN_COLUMNS_FOR_NON_GLOBAL_FEATURES,
  PreviewData,
} from "src/featureCatalogue/Queries/Create/helpers";
import { QueryFormValues } from "src/featureCatalogue/Queries/Create/schema";
import { inferDataTypes } from "src/featureCatalogue/Queries/SQLResonsePreviewTable";

export const useFeatureAutoMapping = (
  form: UseFormReturn<QueryFormValues>,
  isEditMode: boolean = false,
) => {
  const [columnTypes, setColumnTypes] = useState<Record<string, ColumnType>>(
    {},
  );
  const [featureDatatype, setFeatureDatatype] = useState<
    Record<string, FeatureType | undefined>
  >({});

  const windowLength = form.watch("window_length");
  const entityName = form.watch("columns.entities.0.entity_name");

  const updatedColumnTypes = useMemo(() => {
    if (Object.keys(columnTypes).length === 0) return columnTypes;
    const hasEntity = Boolean(entityName);
    const dummyRow = Object.fromEntries(
      Object.keys(columnTypes).map((col) => [col, null]),
    );
    return autoAssignColumnTypes([dummyRow], columnTypes, hasEntity);
  }, [columnTypes, entityName]);

  // Track the previous window_length so the effect can tell whether *this*
  // run was triggered by a window change. Only window changes regenerate
  // existing features' `feature_key` and `name`.
  // The first effect firing is special: in edit mode `window_length` arrives
  // asynchronously (via `useFeatureQuery`), so the initial ref value can
  // differ from the form value through no user action — we must NOT treat
  // that as a window change. But the user might also have changed the window
  // themselves before clicking Run Query, and that case must be detected.
  // RHF's `dirtyFields.window_length` is the right signal: it's reset when
  // the `values` prop syncs (async load) and only becomes true when the
  // user actually edits the field.
  const prevWindowLengthRef = useRef(windowLength);
  const hasEffectFiredRef = useRef(false);

  useEffect(() => {
    if (Object.keys(updatedColumnTypes).length > 0) {
      const entityName = form.getValues("columns.entities.0.entity_name");
      const existingFeatures = form.getValues("columns.features") || [];
      const windowChanged = hasEffectFiredRef.current
        ? prevWindowLengthRef.current !== windowLength
        : !!form.formState.dirtyFields.window_length;
      hasEffectFiredRef.current = true;
      prevWindowLengthRef.current = windowLength;

      // Apply entity column
      const entityColumn = getEntityColumnFromTypes(updatedColumnTypes);
      if (entityColumn && entityName) {
        form.setValue("columns.entities.0.column_name", entityColumn);
      } else if (!entityName) {
        form.setValue("columns.entities.0.column_name", "");
      }

      // Apply grouping key — only one is allowed.
      const groupingKeyColumn = Object.entries(updatedColumnTypes).find(
        ([_, type]) => type === "grouping_key",
      )?.[0];
      form.setValue(
        "columns.grouping_keys",
        groupingKeyColumn ? [groupingKeyColumn] : [],
      );

      // Generate and apply features
      const features = handleFeatureGenerationFromColumnTypes(
        updatedColumnTypes,
        existingFeatures,
        windowLength,
        featureDatatype,
        windowChanged,
      );
      form.setValue("columns.features", features);
    }
  }, [updatedColumnTypes, form, featureDatatype, windowLength]);

  const setColumnType = useCallback((column: string, type: ColumnType) => {
    setColumnTypes((prev) => ({ ...prev, [column]: type }));
  }, []);

  const handleFeatureGeneration = useCallback(
    (previewData: PreviewData): boolean => {
      const columnCount = Object.keys(previewData?.at(0) ?? {}).length;
      // Single column queries cannot have an entity — clear it so all columns become features
      if (columnCount < MIN_COLUMNS_FOR_NON_GLOBAL_FEATURES && !isEditMode) {
        form.setValue("columns.entities.0.entity_name", "");
      }

      const hasEntity = Boolean(
        form.getValues("columns.entities.0.entity_name"),
      );

      // Seed types from saved form state so reopening an existing query
      // doesn't silently demote the saved entity to a feature or wipe
      // grouping_keys (which autoAssignColumnTypes wouldn't otherwise
      // re-derive from preview data).
      const previewColumns = new Set(Object.keys(previewData?.at(0) ?? {}));
      const savedEntityColumn = form.getValues(
        "columns.entities.0.column_name",
      );
      const savedGroupingKeys = form.getValues("columns.grouping_keys") ?? [];
      const savedFeatures = form.getValues("columns.features") ?? [];
      const savedTypes: Record<string, ColumnType> = {
        ...Object.fromEntries(
          savedFeatures
            .filter((f) => previewColumns.has(f.column_name))
            .map((f) => [f.column_name, "feature" as const]),
        ),
        ...Object.fromEntries(
          savedGroupingKeys
            .filter((c) => previewColumns.has(c))
            .map((c) => [c, "grouping_key" as const]),
        ),
        ...(hasEntity &&
        savedEntityColumn &&
        previewColumns.has(savedEntityColumn)
          ? { [savedEntityColumn]: "entity" as const }
          : {}),
      };

      setFeatureDatatype(inferDataTypes(previewData));
      const newColumnTypes = autoAssignColumnTypes(
        previewData,
        savedTypes,
        hasEntity,
      );
      setColumnTypes(newColumnTypes);
      return Object.values(newColumnTypes).some((type) => type === "feature");
    },
    [form, isEditMode],
  );

  return {
    columnTypes: updatedColumnTypes,
    setColumnType,
    handleFeatureGeneration,
  };
};
